"use client";

//region Imports
import React, {useState, useRef, useEffect} from "react";
import type {CropperRef} from "react-advanced-cropper";
import {Cropper, CropperPreview, CircleStencil, RectangleStencil, ImageRestriction, } from "react-advanced-cropper";
import "react-advanced-cropper/dist/style.css";
import 'react-advanced-cropper/dist/themes/corners.css';
import _ from "lodash";

import {cn} from "@isudio/utils";

import {AdjustablePreviewBackground} from "./AdjustablePreviewBackground";
import {AdjustableCropperBackground} from "./AdjustableCropperBackground";
import {BackgroundWrapperNoWheelZoom} from "./BackgroundWrapperNoWheelZoom";
import {RangeSlider, Button, ToolbarTop, ToolbarBottom, useDialogController} from "../";
import type {HeaderElement, FooterElement, BaseDialogProps} from "../";
//endregion Imports

//region Types & Interfaces
//type CropperMode = "crop" | "saturation" | "brightness" | "contrast" | "hue" | "rotate";

interface MediaPickerProps extends BaseDialogProps {
  dialogOptions?: any;
  defaultImage?: string;
  initialImage?: string;
  rounded?: boolean;
  minSize?: number;
  maxSize?: number;
  onSave: (result: string | Blob | null) => void;
  onClose?: () => void;
  cropperProps?: any;
}
//endregion Types & Interfaces

export function MediaPicker({
                              dialogOptions,
                              defaultImage,
                              initialImage,
                              rounded = true,
                              minSize,
                              maxSize,
                              onSave,
                              onClose,
                              cropperProps
                            }: MediaPickerProps) {
  //region Variables
  const {closeDialog} = useDialogController();
  const hasInitializedRef = useRef(false);
  const contentRef = useRef<HTMLDivElement>(null);
  const footerRef = useRef<HTMLDivElement>(null);
  const [footerHeight, setFooterHeight] = useState(0);
  const [isOverflow, setIsOverflow] = useState(false);
  const [isAtBottom, setIsAtBottom] = useState(false);
  const showFooter = !isOverflow || isAtBottom;
  const {name} = dialogOptions;
  const className = name.toLowerCase().replace(/ /g, "-");

  const [propsChanged, setPropsChanged] = useState(false);
  const [layoutChanged, setLayoutChanged] = useState(false);
  const [trackChanges, setTrackChanges] = useState(false);
  const [imgRemoved, setImgRemoved] = useState(false);

  const [baseImage, setBaseImage] = useState<string | null>(null);
  const [currentImage, setCurrentImage] = useState<string | null>(null);
  const [previewImage, setPreviewImage] = useState<string | null>(null);

  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const cropperRef = useRef<CropperRef>(null);
  const [cropperKey, setCropperKey] = useState(0);

  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [cameraMode, setCameraMode] = useState(false);
  const [stream, setStream] = useState<MediaStream | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);

  const [mode, setMode] = useState("crop");
  const [cropperEnabled, setCropperEnabled] = useState((currentImage !== defaultImage && mode === "crop"));
  const [adjustments, setAdjustments] = useState<Record<any, number>>({
    brightness: 0,
    hue: 0,
    saturation: 0,
    contrast: 0,
  });
  //endregion Variables

  //region Event Handlers
  const openFile = (): any => fileInputRef.current?.click();

  const onSelectFile = (e: any) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files?.[0];
      if (file) {
        if (minSize && file.size < minSize * 1024 * 1024) {
          _.toastError(`Please select a file which is at least ${minSize * 1024}KB in size.`);
          fileInputRef.current!.value = "";
          return;
        }

        if (maxSize && file.size > maxSize * 1024 * 1024) {
          _.toastError(`Please select a file which is less than ${maxSize}MB in size.`);
          fileInputRef.current!.value = "";
          return;
        }

        setPropsChanged(false);
        setLayoutChanged(false);
        setTrackChanges(false);

        const reader = new FileReader();
        reader.addEventListener("load", () => {
          const img = reader.result as string;
          fileInputRef.current!.value = "";

          setBaseImage(img);
          setCurrentImage(img);
          setMode("crop");
          hasInitializedRef.current = false;
        });
        reader.readAsDataURL(e.target.files[0]);
      }
    }
  };

  const startCamera = async () => {
    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: true,
      });
      setStream(mediaStream);
      setCameraMode(true);
    } catch (err) {
      console.error("Camera error", err);
    }
  };

  const captureFromCamera = () => {
    if (!videoRef.current) return;

    setPropsChanged(false);
    setLayoutChanged(false);
    setTrackChanges(false);

    const canvas = document.createElement("canvas");
    canvas.width = videoRef.current.videoWidth;
    canvas.height = videoRef.current.videoHeight;
    const ctx = canvas.getContext("2d");
    if (ctx) ctx.drawImage(videoRef.current, 0, 0);
    const dataUrl = canvas.toDataURL("image/png");
    stopCamera();

    setBaseImage(dataUrl);
    setCurrentImage(dataUrl);
    setMode("crop");
    hasInitializedRef.current = false;
  };

  const stopCamera = () => {
    if (stream) {
      stream.getTracks().forEach((t) => t.stop());
      setStream(null);
    }
    setCameraMode(false);
  };

  const onReady = () => {
    if (_.isBrowser() && _.isDebug()) {
      (window as any).$cropper = cropperRef.current;
    }
  }

  const onCanvasReady = (canvas: HTMLCanvasElement, _coords: any) => {
    canvasRef.current = canvas;

    if (hasInitializedRef.current) return;
    const t = setTimeout(() => {
      setPropsChanged(false);
      setLayoutChanged(false);
      setTrackChanges(true);
      hasInitializedRef.current = true;
      clearTimeout(t);
    }, 300)
  }

  const onPreviewReady = (_canvas: HTMLCanvasElement) => {
    if (_canvas) {
      const dataUrl = _canvas.toDataURL("image/png");
      setPreviewImage(dataUrl);
    }
  }

  const onUpdate = () => {
    if (!trackChanges) return;

    if (cropperRef.current) {
      const canvas = cropperRef.current.getCanvas();
      if (canvas) {
        setPreviewImage(canvas.toDataURL("image/png"));
      }
    }
    setLayoutChanged(true);
  };

  const isTouchInsideCanvas = (e: React.TouchEvent) => {
    const canvas = canvasRef.current;
    if (!canvas) return false;

    const rect = canvas.getBoundingClientRect();
    const touch = e.touches[0];
    if (!touch) return false;

    return (
      touch.clientX >= rect.left &&
      touch.clientX <= rect.right &&
      touch.clientY >= rect.top &&
      touch.clientY <= rect.bottom
    );
  };

  const onTouchStart = (e: React.TouchEvent) => {
    if (!isTouchInsideCanvas(e)) {
      e.stopPropagation();
      return;
    }
  };

  const onValueChange = (values: number[]) => {
    if (!trackChanges) return;
    const [value] = values;
    setAdjustments((previousValue) => {
      const newAdjustments = {
        ...previousValue,
        [mode]: value ?? 0,
      };

      const hasPropsChanged = Object.values(newAdjustments).some((el) => el !== 0);
      setPropsChanged(hasPropsChanged);

      return newAdjustments;
    });
  }

  const onReset = () => {
    setPropsChanged(false);
    setLayoutChanged(false);
    setTrackChanges(false);
    setMode("crop");
    setAdjustments({
      brightness: 0,
      hue: 0,
      saturation: 0,
      contrast: 0,
    });
    setCurrentImage(baseImage + '');
    setTrackChanges(baseImage !== defaultImage);
    setCropperKey((prev) => prev + 1);
    hasInitializedRef.current = false;
  };

  const onRemove = () => {
    setMode("crop");
    setAdjustments({
      brightness: 0,
      hue: 0,
      saturation: 0,
      contrast: 0,
    });
    setPropsChanged(false);
    setLayoutChanged(false);
    setTrackChanges(false);
    setCurrentImage(defaultImage + '');
    setBaseImage(defaultImage + '');
    setImgRemoved(initialImage !== defaultImage);
    setCropperKey((prev) => prev + 1);
  };

  const onFlip = (direction: string) => {
    if (!trackChanges) return;
      if (cropperRef.current) {
      const cropper = cropperRef.current;
      cropper.flipImage(direction === 'horizontal', direction === 'vertical');
      setLayoutChanged(true);
    }
  };

  const onRotate = (angle: number) => {
    if (!trackChanges) return;
    if (cropperRef.current) {
      const cropper = cropperRef.current;
      cropper.rotateImage(angle);
      setLayoutChanged(true);
    }
  };

  const saveImage = async () => {
    if (!cropperRef?.current) return;
    const base64 = cropperRef.current.getCanvas()?.toDataURL("image/png") as string;
    const blob = _.base64ToBlob(base64);
    onSave(currentImage === defaultImage ? defaultImage : blob);
    _onClose();
  };

  const onScroll = () => {
    const el = contentRef.current;
    if (!el) return;

    const distanceFromBottom =
      el.scrollHeight - (el.scrollTop + el.clientHeight);

    const SHOW_THRESHOLD = 5;
    const HIDE_THRESHOLD = 50;

    setIsAtBottom(prev => {
      if (!prev && distanceFromBottom <= SHOW_THRESHOLD) return true;
      if (prev && distanceFromBottom > HIDE_THRESHOLD) return false;
      return prev;
    });
  };

  const _onClose = () => {
    onClose?.();
    closeDialog();
  }
  //endregion Event Handlers

  //region Effects
  useEffect(() => {
    if (!footerRef.current) return;

    const el = footerRef.current;

    const updateHeight = () => {
      setFooterHeight(el.scrollHeight);
    };

    updateHeight();

    const observer = new ResizeObserver(() => updateHeight());
    observer.observe(el);

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!contentRef.current) return;

    const el = contentRef.current;

    const checkOverflow = () => {
      requestAnimationFrame(() => {
        setIsOverflow(el.scrollHeight > el.clientHeight);
      });
    };

    checkOverflow();

    const observer = new ResizeObserver(() => checkOverflow());
    observer.observe(el);

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const source = initialImage;
    if (source) {
      setBaseImage(source);
      setCurrentImage(source);
    }
  }, [initialImage]);

  useEffect(() => {
    setCropperEnabled((currentImage !== defaultImage && mode === "crop"));
  }, [currentImage, mode]);

  useEffect(() => {
    if (videoRef.current && stream) {
      videoRef.current.srcObject = stream;
    }
  }, [stream]);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        _onClose();
      }
    };

    window.addEventListener("keydown", handler);

    return () => {
      window.removeEventListener("keydown", handler);
    };
  }, []);
  //endregion Effects

  //region Toolbar Elements
  const headerElements: HeaderElement[] = [
    {
      name: "preview",
      type: "image",
      position: "left",
      icon: previewImage || defaultImage,
      tooltip: "Preview",
      style: "w-10 h-10 rounded-full object-cover shadow-[1px_1px_2px_1px_rgba(105,120,127,1)]",
      hidden: (currentImage === defaultImage),
      onClick: openFile
    },
    {
      name: "camera",
      type: "button",
      position: "left",
      icon: "fa-camera",
      tooltip: "Camera Capture",
      style: "text-white transition duration-200 hover:text-generic-hover hover:scale-120",
      onClick: startCamera
    },
    {
      name: "open",
      type: "button",
      position: "left",
      icon: "fa-image-circle-plus",
      tooltip: "Open Image",
      style: "text-white transition duration-200 hover:text-generic-hover hover:scale-120",
      onClick: openFile
    },
    {
      name: "reset",
      type: "button",
      position: "right",
      icon: "fa-rotate",
      tooltip: "Reset Image",
      style: "text-white scale-90 transition duration-200 hover:text-generic-hover hover:scale-120",
      hidden: !layoutChanged && !propsChanged,
      onClick: onReset
    },
    {
      name: "remove",
      type: "button",
      position: "right",
      icon: "fa-trash-xmark",
      tooltip: "Remove Image",
      style: "text-white scale-95 transition duration-200 hover:text-generic-hover hover:scale-120",
      hidden: (baseImage === defaultImage),
      onClick: onRemove
    },
    {
      name: "save",
      type: "button",
      position: "right",
      icon: "fa-circle-check",
      tooltip: "Set General Picture",
      style: "text-white transition duration-200 hover:text-generic-hover hover:scale-120",
      onClick: saveImage,
      hidden: ((baseImage === initialImage) && !layoutChanged && !propsChanged && !imgRemoved)
    },
    {
      name: "close",
      type: "button",
      position: "right",
      icon: "fa-square-xmark",
      tooltip: "Close",
      style: "text-white scale-115 transition duration-200 hover:text-light-red hover:scale-125",
      onClick: _onClose
    }
  ];

  const footerElements: FooterElement[] = [
    {
      name: "crop",
      type: "button",
      class: "mode",
      mode: "crop",
      icon: "fa-crop",
      tooltip: "Crop",
      style: "text-white transition duration-200 hover:text-generic-hover hover:scale-120",
      disabled: (currentImage === defaultImage),
      onClick: setMode
    },
    {
      name: "hue",
      type: "button",
      class: "mode",
      mode: "hue",
      icon: "fa-palette",
      tooltip: "Hue",
      style: "text-white transition duration-200 hover:text-generic-hover hover:scale-120",
      disabled: (currentImage === defaultImage),
      onClick: setMode
    },
    {
      name: "saturation",
      type: "button",
      class: "mode",
      mode: "saturation",
      icon: "fa-droplet",
      tooltip: "Saturation",
      style: "text-white transition duration-200 hover:text-generic-hover hover:scale-120",
      disabled: (currentImage === defaultImage),
      onClick: setMode
    },
    {
      name: "brightness",
      type: "button",
      class: "mode",
      mode: "brightness",
      icon: "fa-brightness",
      tooltip: "Brightness",
      style: "text-white transition duration-200 hover:text-generic-hover hover:scale-120",
      disabled: (currentImage === defaultImage),
      onClick: setMode
    },
    {
      name: "contrast",
      type: "button",
      class: "mode",
      mode: "contrast",
      icon: "fa-circle-half-stroke",
      tooltip: "Contrast",
      style: "text-white transition duration-200 hover:text-generic-hover hover:scale-120",
      disabled: (currentImage === defaultImage),
      onClick: setMode
    },
    {
      name: "rotateL",
      type: "button",
      class: "action",
      icon: "fa-rotate-left",
      tooltip: "Rotate Left",
      style: "text-white transition duration-200 hover:text-generic-hover hover:scale-120",
      disabled: (currentImage === defaultImage),
      onClick: onRotate,
      onClickParameter: -90
    },
    {
      name: "rotateR",
      type: "button",
      class: "action",
      icon: "fa-rotate-right",
      tooltip: "Rotate Right",
      style: "text-white transition duration-200 hover:text-generic-hover hover:scale-120",
      disabled: (currentImage === defaultImage),
      onClick: onRotate,
      onClickParameter: 90
    },
    {
      name: "flipH",
      type: "button",
      class: "action",
      icon: "fa-reflect-horizontal",
      tooltip: "Flip Horizontal",
      style: "text-white transition duration-200 hover:text-generic-hover hover:scale-120",
      disabled: (currentImage === defaultImage),
      onClick: onFlip,
      onClickParameter: "horizontal"
    },
    {
      name: "flipV",
      type: "button",
      class: "action",
      icon: "fa-reflect-vertical",
      tooltip: "Flip Vertical",
      style: "text-white transition duration-200 hover:text-generic-hover hover:scale-120",
      disabled: (currentImage === defaultImage),
      onClick: onFlip,
      onClickParameter: "vertical"
    }
  ];
  //endregion Toolbar Elements

  //region HTML Elements
  const toolbarTop = (
    <ToolbarTop
      name={`tbt-${className}`}
      elements={headerElements}
      leftToolbarGap="gap-3 md:gap-4"
      rightToolbarGap="gap-3 md:gap-4"
      size="text-xl md:text-2xl"
    />
  );

  const toolbarBottom = (
    <ToolbarBottom
      name={`tbb-${className}`}
      elements={footerElements}
      mode={mode}
      gap="gap-4 xl:gap-6"
      size="text-sm md:text-xl"
    />
  );

  const fileInput = (
    <input
      ref={fileInputRef}
      type="file"
      accept="image/*"
      onChange={onSelectFile}
      className="hidden"
    />
  );

  const cameraCapture = (
    <div
      className="flex flex-col items-center gap-3">
      <video
        ref={videoRef}
        autoPlay
        playsInline
        className="rounded-sm md:rounded-md max-h-80"
      />
      <Button
        onClick={captureFromCamera}>Capture</Button>
      <Button
        variant="secondary"
        onClick={stopCamera}>
        Close
        Camera
      </Button>
    </div>
  );

  const preview = (
    <div style={{ display: "none" }}>
      <CropperPreview
        className={cropperProps.preview.className}
        cropper={cropperRef}
        backgroundComponent={AdjustablePreviewBackground}
        backgroundProps={{...adjustments, onPreviewReady: onPreviewReady}}
      />
    </div>
  );

  const cropper = (
    <div
      className={`${className} flex flex-col gap-4`}>
      <div
        className={`${className}__cropper`}
        onTouchStartCapture={onTouchStart}
        style={{
          touchAction: "pan-y"
        }}
      >
        <Cropper
          key={cropperKey}
          src={currentImage}
          ref={cropperRef}
          className={cn(`
                  cropper w-full aspect-square object-contain
                  max-h-88! [@media(max-height:690px)]:max-h-[calc(100vh-98px)]!`,
            !cropperEnabled && "cropper--disabled"
          )}
          imageRestriction={ImageRestriction.fitArea}
          defaultSize={({ imageSize, visibleArea }) => {
            return {
              width: (visibleArea || imageSize).width,
              height: (visibleArea || imageSize).height,
            }
          }}
          stencilComponent={rounded ? CircleStencil : RectangleStencil}
          stencilProps={{
            movable: cropperEnabled,
            resizable: cropperEnabled,
            lines: cropperEnabled,
            overlayClassName: cn(
              `${className}__cropper-overlay`,
              !cropperEnabled && `${className}__cropper-overlay--faded`
            ),
          }}
          backgroundComponent={AdjustableCropperBackground}
          backgroundProps={{
            ...adjustments,
            onCanvasReady: onCanvasReady
          }}
          backgroundWrapperComponent={BackgroundWrapperNoWheelZoom}
          backgroundWrapperProps={{
            scaleImage: cropperEnabled,
            moveImage: cropperEnabled,
            enableWheelHint: cropperEnabled
          }}
          onUpdate={onUpdate}
          onReady={onReady}
        />
        {preview}
      </div>
    </div>
  );

  const rangeSlider = (
    <RangeSlider
      className="range-slider"
      rangeClassName="range-slider__range"
      wrapperClassName="range-slider__wrapper"
      trackClassName="range-slider__track"
      thumbClassName="range-slider__thumb"
      tooltipClassName="range-slider__tooltip"
      min={-100}
      max={100}
      step={1}
      tooltipMode="always"
      value={[adjustments[mode] ?? 0]}
      onValueChange={onValueChange}
    />
  );

  const content = (
    <div
      ref={contentRef}
      onScroll={onScroll}
      className={`
          content-${className}
          flex-1 px-4 my-4 min-h-0 box-border overflow-y-auto 
          bg-[radial-gradient(circle,#ffffff03_0%,#030b1166_100%)] 
          scrollbar-track-rounded-full scrollbar-thin 
          scrollbar-track-dim-white dark:scrollbar-track-transparent 
          scrollbar-thumb-custom-blue-600 
          scrollbar-hover:scrollbar-thumb-custom-blue-300 
          scrollbar-active:scrollbar-thumb-custom-blue-300
        `}
    >
      {cameraMode && (
        cameraCapture
      )}

      {currentImage && !cameraMode && (
        cropper
      )}

      {mode !== "crop" && (
        rangeSlider
      )}
    </div>
  );

  const footer = (
    <div
      style={{
        height: showFooter ? footerHeight : 0
      }}
      className="overflow-hidden transition-[height] duration-300 ease-in-out"
    >
      <div
        ref={footerRef}
        className="flex flex-col">
        {toolbarBottom}
      </div>
    </div>
  );
  //endregion HTML Elements

  return (
    <div
      className="relative w-full h-full flex flex-col rounded-sm md:rounded-md overflow-hidden"
      onWheel={(e) => e.stopPropagation()}>
      {fileInput}

      {toolbarTop}

      {content}

      {footer}
    </div>
  );
}